import type { IaIndicatorStatus, IaMonitoringDomain } from '../objects/iaMonitoringTypes';
import type { IaCopy } from './iaMonitoringCopy';

/** Ordre de la maquette FD-4711 : une entrée de menu par domaine. */
export const IA_MONITORING_DOMAINS: IaMonitoringDomain[] = ['relevance', 'accuracy', 'comprehension', 'reliability', 'cost', 'usage'];

/** La vue d'ensemble montre cinq tuiles : l'usage n'a pas de seuil, il se lit dans son écran. */
export const IA_MONITORING_TILE_DOMAINS: IaMonitoringDomain[] = ['relevance', 'accuracy', 'comprehension', 'reliability', 'cost'];

const c = (key: string, fallback: string): IaCopy => [`ia-monitoring.${key}`, fallback];

export const IA_MONITORING_DOMAIN_LABELS: Record<IaMonitoringDomain, IaCopy> = {
    relevance: c('relevance', 'Pertinence'),
    accuracy: c('accuracy', 'Justesse'),
    comprehension: c('comprehension', 'Compréhension'),
    reliability: c('reliability', 'Fiabilité'),
    cost: c('cost', 'Coût'),
    usage: c('usage', 'Usage')
};

export const IA_MONITORING_STATUS_LABELS: Record<IaIndicatorStatus, IaCopy> = {
    ok: c('status-ok', 'OK'),
    watch: c('status-watch', 'À surveiller'),
    alert: c('status-alert', 'Alerte'),
    follow: c('status-follow', 'Suivi'),
    no_data: c('status-no-data', 'Pas de donnée')
};

export const IA_MONITORING_STATUS_COLORS: Record<IaIndicatorStatus, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    ok: 'success',
    watch: 'warning',
    alert: 'error',
    follow: 'info',
    no_data: 'default'
};

/** Couleur de thème d'un statut, pour une barre, un point, un liseré. */
export const IA_MONITORING_STATUS_TONES: Record<IaIndicatorStatus, string> = {
    ok: 'success.main',
    watch: 'warning.main',
    alert: 'error.main',
    follow: 'info.main',
    no_data: 'text.disabled'
};

/** Périodes proposées, en jours (aujourd'hui compris). */
export const IA_MONITORING_PERIODS = [7, 30, 90] as const;
export const IA_MONITORING_DEFAULT_PERIOD = 7;

/** Types de client du filtre (`client_client.type`) ; vide = TO + agences. */
export const IA_MONITORING_CLIENT_TYPES: [number | '', IaCopy][] = [
    ['', c('client-type-all', 'TO + agences')],
    [1, c('client-type-to', 'TO')],
    [2, c('client-type-agency', 'Agences')]
];

/** Libellés des valeurs que l'agent renvoie (familles, niveaux, étapes, gammes, natures, erreurs). */
export const IA_MONITORING_ROW_LABELS: Record<string, IaCopy> = {
    flight: c('family-flight', 'Vol'),
    train: c('family-train', 'Train'),
    car: c('family-car', 'Voiture'),
    accommodation: c('family-accommodation', 'Hébergement'),
    activity: c('family-activity', 'Activité'),
    transfer: c('family-transfer', 'Transfert'),
    destination: c('family-destination', 'Destination'),
    other: c('family-other', 'Autres'),
    exact: c('level-exact', 'Parfait'),
    strong: c('level-strong', 'Bon'),
    partial: c('level-partial', 'Partiel'),
    weak: c('level-weak', 'Faible'),
    composed: c('path-composed', 'Envoyé du premier coup'),
    literal: c('path-literal', 'Repli littéral'),
    fused: c('path-fused', 'Fusion des passages'),
    understand: c('stage-understand', 'Comprendre le message'),
    build: c('stage-build', 'Construire l’itinéraire'),
    search: c('stage-search', 'Chercher les produits'),
    write: c('stage-write', 'Rédiger et vérifier'),
    budget: c('class-budget', 'Léger (comprendre, vérifier)'),
    standard: c('class-standard', 'Standard (construire)'),
    premium: c('class-premium', 'Haut de gamme'),
    creation: c('nature-creation', 'Création'),
    itinerary: c('nature-itinerary', 'Itinéraire'),
    products: c('nature-products', 'Produits'),
    question: c('nature-question', 'Question'),
    social: c('nature-social', 'Échange'),
    invented_number: c('rejection-invented-number', 'Chiffre inventé'),
    missing_name: c('rejection-missing-name', 'Nom absent'),
    too_long: c('rejection-too-long', 'Trop long'),
    label_copied: c('rejection-label-copied', 'Étiquette recopiée'),
    untranslated: c('rejection-untranslated', 'Non traduit'),
    judge: c('rejection-judge', 'Refusé par le juge'),
    empty: c('rejection-empty', 'Message vide')
};

/** Ce que précise une ligne, à côté de son libellé — propre à CHAQUE panneau : « Autres » n'a pas
 *  le même sens dans les étapes et dans les natures de demande. */
export const IA_MONITORING_ROW_HINTS: Record<string, Record<string, IaCopy>> = {
    stages: {
        understand: c('stage-understand-hint', '1re étape'),
        build: c('stage-build-hint', 'suggestion'),
        search: c('stage-search-hint', 'par famille'),
        write: c('stage-write-hint', 'message + juge'),
        other: c('stage-other-hint', 'résumés, traductions')
    },
    natures: {
        products: c('nature-products-hint', 'ajouter ou changer un hôtel, un vol…'),
        itinerary: c('nature-itinerary-hint', 'modifier les étapes ou les nuits'),
        creation: c('nature-creation-hint', 'nouveau dossier'),
        question: c('nature-question-hint', 'demande d’information'),
        social: c('nature-social-hint', 'bonjour, merci…')
    },
    rejection_categories: {
        invented_number: c('rejection-invented-number-hint', 'un prix ou un nombre qu’aucune donnée ne donne'),
        missing_name: c('rejection-missing-name-hint', 'un hôtel ou un lieu cité mais pas dans les résultats'),
        too_long: c('rejection-too-long-hint', 'le message dépasse ce qu’il y a à dire'),
        label_copied: c('rejection-label-copied-hint', 'un code interne affiché au client')
    }
};
